import React from 'react';
import { Globe, Shield, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { VisibilityChoice as VisibilityType } from './contexts/types';
import { AIReviewUsage } from '@/types/subscription';

interface VisibilityChoiceProps {
  visibilityChoice: VisibilityType;
  setVisibilityChoice: (visibility: VisibilityType) => void;
  aiReviewUsage: AIReviewUsage | null;
}

export const VisibilityChoice: React.FC<VisibilityChoiceProps> = ({
  visibilityChoice,
  setVisibilityChoice,
  aiReviewUsage
}) => {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold">What would you like to do?</h2>
        <p className="text-muted-foreground">
          Choose the path that fits where your paper is right now
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        {/* Confidential Feedback Option (Primary) */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg hover:border-primary/50 ${
            visibilityChoice === 'private' 
              ? 'border-primary shadow-md ring-2 ring-primary/20' 
              : 'border-border'
          }`}
          onClick={() => setVisibilityChoice('private')}
        >
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/20">
                  <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Get confidential feedback</h3>
                  <Badge variant="outline" className="mt-1 bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400">
                    Only you can see it
                  </Badge>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                Upload your manuscript securely. Get instant AI-powered feedback to strengthen your paper before submission.
              </p>
              <ul className="space-y-1.5 ml-4 list-disc">
                <li>Only you can see your draft — stored & processed in the EU, never shared</li>
                <li>Receive detailed AI feedback on structure and clarity</li>
                <li>Upload revised versions and track improvements</li>
                <li>Make it public later, only if you choose to</li>
              </ul>
            </div>

            {aiReviewUsage && (
              <div className="pt-3 border-t border-border/50">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>AI reviews remaining:</span>
                  <span className="font-medium text-foreground">
                    {aiReviewUsage.remaining} / {aiReviewUsage.limit}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Community Sharing Option */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg hover:border-primary/50 ${
            visibilityChoice === 'public' 
              ? 'border-primary shadow-md ring-2 ring-primary/20' 
              : 'border-border'
          }`}
          onClick={() => setVisibilityChoice('public')}
        >
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/20">
                  <Globe className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Share with the community</h3>
                  <Badge variant="outline" className="mt-1 bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400">
                    Open for discussion
                  </Badge>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                Bring a paper you're ready to share openly — from arXiv or your computer.
              </p>
              <ul className="space-y-1.5 ml-4 list-disc">
                <li>Link papers from arXiv or other preprint servers</li>
                <li>Upload your own PDF for public review</li>
                <li>Get peer feedback and discussions</li>
                <li>Build your academic reputation</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trust Signal */}
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="h-4 w-4 text-blue-500" />
        <span>Your uploads are private by default. Nothing is shared without your explicit action.</span>
      </div>
    </div>
  );
};
